import React, { memo, useState, useCallback, useMemo, useEffect } from "react";
import cls from "classnames";
import { useT, useCtx, withRedux, WithReduxHocPropsType } from "@al/next/al-ex";
import { Popup, WebpImage, Button, Stepper, Toast } from "@al/akui-mobile";
import { PriceTools, CountryCode } from "@al/base-lib";
import { EventType } from "@al/event-tracking-sensor-analytics";

import linkTo from "~/common/utils/linkTo";
import Iconfont from "~/components/ui/Iconfont";
import { tracker } from "~/common/tracker";

import { getTrackerSaReportInfo } from "../../../../sensor-config";
import FunctionItem from "../item";
import SkuSelect, { SelectAttrType } from "../../../components/SkuSelect";
import {
  ProductDetailType,
  SkuInfoType,
  SkuPriceType,
  AddressItemType,
  SaleStockInfoType,
  InstallmentPlanItemType,
  OrderPayTypeEnum,
  ChannelType,
} from "../../../types";
import redux from "../../../redux";
import { adjustLink } from "../../../constants";

import InstallmentPlans from "./InstallmentPlans";
import { joinProperties } from "./utils";
import styles from "./styles.scss";

interface Props extends WithReduxHocPropsType {
  detail: ProductDetailType;
  currentSku: SkuInfoType;
  skuPrice: SkuPriceType;
  defaultAddress: AddressItemType;
  saleStockInfos: SaleStockInfoType[];
  visible: boolean;
  installmentPlans: InstallmentPlanItemType[];
  skuAction: number;
  deepLink: string;
  openAppLink: string;
}

const Standard: React.FC<Props> = (props) => {
  const t = useT();
  const ctx = useCtx();
  const countryCode = ctx.locale.countryCode;
  const { detail, currentSku, installmentPlans } = props;
  const [selectPlan, setSelectPlan] = useState<InstallmentPlanItemType>();
  const [qty, setQty] = useState<number>(1);
  const [selectAttrs, setSelectAttrs] = useState<SelectAttrType | null>();
  const { promotion, couponPrice, sellingPrice } = props.skuPrice || {};

  // 当前选中 sku 的库存
  const currentSkuStock = useMemo(
    () => props.saleStockInfos?.find((item) => item.skuId === currentSku?.skuId)?.quantity || 0,
    [currentSku, props.saleStockInfos]
  );

  useEffect(() => {
    currentSku &&
      currentSkuStock > 0 &&
      setSelectAttrs({
        isSingleSku: detail.skuInfos.length === 1,
        item: currentSku,
        standardNames: joinProperties(detail.spuSaleInfos, currentSku),
      });
  }, [currentSku, currentSkuStock]);

  useEffect(() => {
    const channel = (ctx.query.channel as unknown) as ChannelType;
    const spuId = ctx.query.spuId;
    const skuId = ctx.query.skuId;
    const href =
      adjustLink[channel]?.(props.deepLink, spuId, currentSku?.skuId || skuId || detail.skuInfos?.[0]?.skuId) ||
      adjustLink[ChannelType.Normal](props.deepLink, spuId, currentSku?.skuId || skuId || detail.skuInfos?.[0]?.skuId);
    props.dispatch(redux.actions.setOpenAppLink(href));
  }, [currentSku, props.deepLink, detail.skuInfos]);

  const onSelectAttrs = useCallback(
    (attrs: SelectAttrType | null): void => {
      setSelectAttrs(attrs);
      tracker.popClick({
        sa: {
          ...getTrackerSaReportInfo({ page: "homePage", popName: "skuPop", eventName: "selectAttributes" }),
          skuAction: props.skuAction,
        },
      });
      // 切换数量时重新请求 sku-price 和 运费
      attrs?.item?.skuId && props.dispatch(redux.actions.updateSkuPrice({ count: qty, skuId: attrs?.item?.skuId }));
    },
    [qty, props.skuAction]
  );

  const handleJump = useCallback(async (): Promise<void> => {
    props.dispatch(redux.actions.setSkuSelectorVisible(false));
    props?.openAppLink && linkTo(props.openAppLink);
  }, [props.openAppLink]);

  const onConfirm = useCallback(async (): Promise<void> => {
    tracker.popClick({
      sa: {
        ...getTrackerSaReportInfo({ page: "homePage", popName: "skuPop", eventName: "clickPurchase" }),
        skuAction: props.skuAction,
      },
    });
    handleJump();
  }, [props.skuAction, handleJump]);

  const onAddCart = useCallback(async (): Promise<void> => {
    tracker.popClick({
      sa: {
        ...getTrackerSaReportInfo({ page: "homePage", popName: "skuPop", eventName: "clickAddCart" }),
        skuAction: props.skuAction,
      },
    });
    handleJump();
  }, [props.skuAction, handleJump]);

  const showSkuSelect = useCallback((): void => {
    props.dispatch(redux.actions.setSkuSelectorVisible(true));
    ctx.store.dispatch(redux.actions.setSkuAction(2));
    tracker.expose({
      sa: {
        event: EventType.PopView,
        ...getTrackerSaReportInfo({ page: "homePage", popName: "skuPop" }),
        skuAction: 2,
      },
    });
  }, []);

  const closeSkuSelect = useCallback((): void => {
    props.dispatch(redux.actions.setSkuSelectorVisible(false));
    tracker.popClick({
      sa: {
        ...getTrackerSaReportInfo({ page: "homePage", popName: "skuPop", eventName: "skuClose" }),
        skuAction: props.skuAction,
      },
    });
  }, [props.skuAction]);

  // 计算出来有库存的sku
  const computedStockSku = useMemo(
    () =>
      detail.skuInfos?.filter(
        (item) => !!props.saleStockInfos?.find((sku) => sku.skuId === item.skuId && sku.quantity > 0)
      ),
    [detail, props.saleStockInfos]
  );

  // 数量选择器上限, 上限是 99 个
  const stepperMax = useMemo(() => Math.min(currentSkuStock ?? 99, promotion?.stock ?? 99, 99), [
    currentSkuStock,
    promotion,
  ]);

  // 活动限购
  const promotionLimit = useMemo(() => promotion?.limit || 0, [promotion]);

  // 选中 sku 属性joinProperties(detail.spuSaleInfos, currentSku)
  const property = useMemo(
    () =>
      (selectAttrs?.standardNames?.length === detail.spuSaleInfos.length ? selectAttrs?.standardNames : []).join(", "),
    [selectAttrs]
  );
  // 外层兜底显示选中 sku 属性。
  const previousProperty = useMemo(() => (joinProperties(detail.spuSaleInfos, currentSku) || []).join(", "), [
    detail,
    currentSku,
  ]);

  // 不在配送区域内的话，不显示已选择的 sku 属性，sku 选择禁用，不显示分期，下单按钮禁用
  const isOutOfArea = useMemo(
    () =>
      currentSkuStock === 0 ||
      props.skuPrice === null ||
      props.skuPrice?.saleStockInfos?.every((item) => item.quantity === 0),
    [currentSkuStock, props.skuPrice]
  );

  const onQtyChange = useCallback(
    (val: number): void => {
      if (detail.spuSaleInfos?.length && !property?.length) {
        Toast.info(t("请选择规格"));
        return;
      }
      setQty(val);
      // 切换数量时重新请求 sku-price 和 运费
      props.dispatch(redux.actions.updateSkuPrice({ count: val }));
      tracker.popClick({
        sa: {
          ...getTrackerSaReportInfo({ page: "homePage", popName: "skuPop", eventName: "skuChangeQut" }),
          skuAction: props.skuAction,
        },
      });
    },
    [property, props.skuAction]
  );

  return (
    <>
      {detail?.skuInfos?.length > 0 && detail.spuSaleInfos?.length > 0 ? (
        <FunctionItem
          title={t("商品规格")}
          subTitle={property || previousProperty}
          subTitleClassName={styles.sub_content}
          onClick={showSkuSelect}
        />
      ) : null}
      <Popup className={styles.popup_wrap} onMaskClick={closeSkuSelect} visible={props.visible}>
        <div className={styles.wrapper}>
          {detail && (
            <div className={styles.header}>
              {/* 商品图 */}
              <div className={styles.image_wrapper}>
                <WebpImage className={styles.img} source={selectAttrs?.item?.image || detail.indexImage} width={75} />
              </div>
              {/* 选中的规格信息 */}
              <div className={styles.info}>
                <div className={styles.product_price}>
                  {PriceTools.formatPrice(
                    ctx.locale.countryCode as CountryCode,
                    sellingPrice || currentSku?.originalPrice,
                    "price"
                  )}
                </div>
                {/* 券后价, 全款不显示券后价 */}
                {couponPrice && selectPlan?.orderPayType !== OrderPayTypeEnum.FullPay && (
                  <div className={styles.product_coupon_price}>
                    {PriceTools.formatPrice(ctx.locale.countryCode as CountryCode, couponPrice, "price")} {t("券后价")}
                  </div>
                )}
                {!installmentPlans ||
                installmentPlans?.length === 0 ||
                selectAttrs?.item === undefined ||
                (installmentPlans?.length === 1 && installmentPlans[0].periods === 0) ? null : (
                  <div className={styles.down_payment_info}>
                    <div>
                      {selectPlan?.periods ? (
                        <>
                          <div>
                            {t("分期付款")}{" "}
                            <span className={styles.price}>
                              {PriceTools.formatPrice(countryCode as CountryCode, selectPlan.monthlyPay, "price")}
                            </span>
                            <span> X</span>
                            <span> {t("{{x}}月", { x: selectPlan.periods })}</span>
                          </div>
                          <div>
                            {t("首付")}{" "}
                            {PriceTools.formatPrice(
                              ctx.locale.countryCode as CountryCode,
                              selectPlan?.periods ? selectPlan?.downPayment : installmentPlans[0].downPayment
                            )}
                          </div>
                        </>
                      ) : (
                        <div>{t("可选择分期支付哦，最高可分{{x}}期", { x: installmentPlans[0].periods })}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {/* 关闭按钮 */}
              <Iconfont className={styles.close_icon} name="close" onClick={closeSkuSelect} />
            </div>
          )}
          <div className={styles.bar} />
          <div className={styles.box_body}>
            {!isOutOfArea && ((!selectAttrs?.isSingleSku && currentSkuStock > 0) || property) && (
              <div className={styles.product_standard}>
                {property ? `${t("已选择：")} ${property}` : `${t("请选择")} ${selectAttrs?.unselectPropNames ?? ""}`}
              </div>
            )}
            {/* 商品sku选择器 */}
            {detail?.skuInfos?.length && (
              <SkuSelect
                skuInfos={computedStockSku}
                defaultSelect={isOutOfArea ? null : selectAttrs?.standardNames}
                spuSaleInfos={detail.spuSaleInfos}
                onSelect={onSelectAttrs}
              />
            )}
            {/* 数量选择器 */}
            <div className={styles.qty}>
              <div className={styles.props_title}>{t("数量")}</div>
              <Stepper
                className={styles.stepper}
                inputReadOnly
                min={1}
                max={stepperMax}
                value={qty}
                onChange={onQtyChange}
              />
            </div>
            {/* 有促销，且当前选择数量大于限购数量, limit 0 表示不限购 */}
            {property && promotionLimit > 0 && qty > promotionLimit ? (
              <div className={styles.left_count}>
                {t("该商品最多可用活动价购买{{x}}件，超出数量每件{{xxx}}", {
                  x: promotionLimit,
                  xxx: PriceTools.formatPrice(ctx.locale.countryCode as CountryCode, currentSku.originalPrice, "price"),
                })}
              </div>
            ) : null}
            {/* 库存小于 10 个的时候提示 */}
            {property && stepperMax < 10 ? (
              <div className={styles.left_count}>{t("仅剩", { x: stepperMax })}</div>
            ) : null}
            {!isOutOfArea && <InstallmentPlans selectAttrs={selectAttrs} onChange={setSelectPlan} />}
          </div>
          <div className={styles.bar} />
          <div className={styles.btn_wrap}>
            <Button
              className={cls(styles.btn, styles.add_cart)}
              color="primary"
              disabled={isOutOfArea || (!property && detail?.spuSaleInfos.length > 0) || stepperMax === 0}
              type="button"
              onClick={onAddCart}
            >
              {t("加入购物车")}
            </Button>
            <Button
              className={styles.btn}
              color="primary"
              disabled={isOutOfArea || (!property && detail?.spuSaleInfos.length > 0) || stepperMax === 0}
              type="button"
              onClick={onConfirm}
            >
              {t("立即购买")}
            </Button>
          </div>
        </div>
      </Popup>
    </>
  );
};

export default withRedux((state) => ({
  currentSku: state.static.currentSku,
  skuPrice: state.static.skuPrice,
  visible: state.static.showSkuSelector,
  defaultAddress: state.static.defaultAddress,
  saleStockInfos: state.static.saleStockInfos,
  installmentPlans: state.static.installmentPlans,
  skuAction: state.static.skuAction,
  deepLink: state.static.deepLink,
  openAppLink: state.static.openAppLink,
}))(memo(Standard));
